import { Avatar, Badge, Icon, IconButton } from '@ds/core';
import { Menu } from '@ds/navigation';
import type { DataTableColumn } from '@ds/data';
import { dataSimples, moeda, proximoContato } from '@/comuns/formato';
import { estiloDaEtiqueta } from '@/comuns/contraste';
import { EtiquetaChip } from '@/comuns/EtiquetaChip';
import { TOM_DA_SITUACAO } from '@/data/funis';
import type {
    AcessoDoMembro, Convenio, EtiquetaAplicada, Fornecedor, Lead, MembroDaEquipe, Paciente, PrestadorDeServico, SituacaoDoPaciente,
} from '@/data/tipos';

const SITUACAO: Record<SituacaoDoPaciente, { label: string; tom: 'success' | 'warning' | 'danger' }> = {
    ativo: { label: 'Ativo', tom: 'success' },
    pendente: { label: 'Pendente', tom: 'warning' },
    vencido: { label: 'Vencido', tom: 'danger' },
};

const ACESSO: Record<AcessoDoMembro, { label: string; tom: 'success' | 'warning' | 'neutral' }> = {
    'ativo': { label: 'Ativo', tom: 'success' },
    'convite-pendente': { label: 'Convite pendente', tom: 'warning' },
    'desativado': { label: 'Desativado', tom: 'neutral' },
};

interface ComNomeETelefone {
    nome: string;
    telefone: string;
    /** Quem tem (paciente e lead) mostra; quem não tem (fornecedor, equipe) não muda de forma. */
    etiquetas?: EtiquetaAplicada[];
}

/**
 * Nome, telefone e as etiquetas da pessoa.
 *
 * As etiquetas entram AQUI, e não numa coluna própria, porque são da pessoa e se leem
 * junto do nome — coluna separada empurraria as outras e ficaria vazia para fornecedor e
 * equipe, que não têm etiqueta. Só mostra: quem edita é a ficha.
 */
function identidade<Row extends ComNomeETelefone>(header: string): DataTableColumn<Row> {
    return {
        key: 'nome', header, sortable: true,
        render: (r) => (
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Avatar name={r.nome} size="sm" />
                <span>
                    <span style={{ display: 'block', fontWeight: 600 }}>{r.nome}</span>
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)' }}>{r.telefone}</span>
                    {(r.etiquetas?.length ?? 0) > 0 && (
                        <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                            {r.etiquetas?.map((e) => <EtiquetaChip key={e.id} nome={e.nome} cor={e.cor} />)}
                        </span>
                    )}
                </span>
            </span>
        ),
    };
}

/** Ações da linha. O lápis abre em edição; a linha inteira abre em leitura —
 *  são intenções diferentes, e uma não deve ser adivinhada pela outra. */
function acoes<Row extends { id: string; nome: string }>(
    itens: { id: string; label: string; icon?: string; tone?: 'default' | 'danger'; divider?: boolean }[],
    aoEditar: (r: Row) => void,
    aoEscolher: (id: string, r: Row) => void,
): DataTableColumn<Row> {
    return {
        key: 'acoes', header: '', align: 'right', width: 84,
        render: (r) => (
            <span style={{ display: 'inline-flex', gap: 2 }} data-parar-linha>
                <IconButton size="sm" icon={<Icon name="pencil" size={14} />} label={`Editar ${r.nome}`} onClick={() => aoEditar(r)} />
                <Menu
                    align="right" width={210}
                    onSelect={(id) => aoEscolher(id, r)}
                    trigger={<IconButton size="sm" icon={<Icon name="ellipsis" size={15} />} label={`Mais ações — ${r.nome}`} />}
                    items={itens}
                />
            </span>
        ),
    };
}

export function colunasDePacientes(convenios: Convenio[]): DataTableColumn<Paciente>[] {
    const porId = new Map(convenios.map((c) => [c.id, c]));
    return [
        identidade<Paciente>('Paciente'),
        { key: 'nascimento', header: 'Nascimento', render: (r) => dataSimples(r.nascimento) },
        { key: 'profissional', header: 'Profissional' },
        { key: 'ultimoAtendimentoEm', header: 'Último atendimento', sortable: true, render: (r) => dataSimples(r.ultimoAtendimentoEm) },
        {
            /* A recepção filtra por convênio antes de ligar, então o plano
               precisa aparecer na listagem, sem abrir a ficha. */
            key: 'convenioId', header: 'Plano',
            render: (r) => {
                const plano = r.convenioId ? porId.get(r.convenioId) : undefined;
                if (!plano) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
                return (
                    <span
                        title={plano.tipo}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6, height: 22, padding: '0 9px',
                            borderRadius: 'var(--radius-pill)', ...estiloDaEtiqueta(plano.cor),
                            fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                        }}
                    >
                        <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: plano.cor }} />
                        {plano.nome}
                    </span>
                );
            },
        },
        { key: 'situacao', header: 'Situação', render: (r) => <Badge tone={SITUACAO[r.situacao].tom} dot>{SITUACAO[r.situacao].label}</Badge> },
        { key: 'emAbertoCentavos', header: 'Em aberto', align: 'right', render: (r) => (r.emAbertoCentavos > 0 ? moeda(r.emAbertoCentavos) : '—') },
    ];
}

export function colunasDeLeads(
    aoEditar: (l: Lead) => void,
    aoEscolher: (id: string, l: Lead) => void,
    hojeIso?: string,
): DataTableColumn<Lead>[] {
    return [
        identidade<Lead>('Lead'),
        { key: 'origem', header: 'Origem' },
        { key: 'interesse', header: 'Interesse' },
        { key: 'responsavel', header: 'Responsável' },
        {
            key: 'proximoContatoEm', header: 'Próximo contato',
            render: (r) => {
                const texto = proximoContato(r.proximoContatoEm, hojeIso);
                const atrasado = texto.startsWith('Atrasado');
                return <span style={{ color: atrasado ? 'var(--danger-strong)' : 'var(--text-primary)', fontWeight: atrasado ? 600 : 400 }}>{texto}</span>;
            },
        },
        { key: 'etapa', header: 'Etapa', render: (r) => <Badge tone={TOM_DA_SITUACAO[r.situacao]} dot>{r.etapa}</Badge> },
        { key: 'valorEstimadoCentavos', header: 'Valor estimado', align: 'right', render: (r) => moeda(r.valorEstimadoCentavos) },
        acoes<Lead>(
            [
                { id: 'abrir', label: 'Ver detalhes', icon: 'eye' },
                { id: 'editar', label: 'Editar lead', icon: 'pencil' },
                { id: 'funil', label: 'Ver no funil', icon: 'target' },
                { id: 'mensagem', label: 'Enviar mensagem', icon: 'message-circle' },
                { id: 'divisor', label: '', divider: true },
                { id: 'perdido', label: 'Marcar como perdido', icon: 'x', tone: 'danger' },
            ],
            aoEditar, aoEscolher,
        ),
    ];
}

export function colunasDeMembros(
    aoEditar: (m: MembroDaEquipe) => void,
    aoEscolher: (id: string, m: MembroDaEquipe) => void,
): DataTableColumn<MembroDaEquipe>[] {
    return [
        identidade<MembroDaEquipe>('Profissional'),
        { key: 'papel', header: 'Função' },
        { key: 'registro', header: 'Registro', render: (r) => r.registro ?? '—' },
        { key: 'especialidades', header: 'Especialidades' },
        { key: 'cargaSemanal', header: 'Carga semanal', render: (r) => r.cargaSemanal ?? '—' },
        { key: 'acesso', header: 'Acesso', render: (r) => <Badge tone={ACESSO[r.acesso].tom} dot>{ACESSO[r.acesso].label}</Badge> },
        acoes<MembroDaEquipe>(
            [
                { id: 'abrir', label: 'Ver detalhes', icon: 'eye' },
                { id: 'editar', label: 'Editar profissional', icon: 'pencil' },
                { id: 'agenda', label: 'Ver agenda', icon: 'calendar-days' },
                { id: 'divisor', label: '', divider: true },
                { id: 'desativar', label: 'Desativar acesso', icon: 'circle-slash', tone: 'danger' },
            ],
            aoEditar, aoEscolher,
        ),
    ];
}

const ATIVO = (ativo: boolean) => <Badge tone={ativo ? 'success' : 'neutral'} dot>{ativo ? 'Ativo' : 'Inativo'}</Badge>;
const VAZIO = <span style={{ color: 'var(--text-tertiary)' }}>—</span>;

/** O mesmo cadastro de Compras e Estoque. */
export function colunasDeFornecedores(): DataTableColumn<Fornecedor>[] {
    return [
        {
            key: 'nome', header: 'Fornecedor', sortable: true,
            render: (f) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar name={f.nome} size="sm" />
                    <span>
                        <span style={{ display: 'block', fontWeight: 600 }}>{f.nome}</span>
                        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)' }}>{f.cnpj || 'sem CNPJ'}</span>
                    </span>
                </span>
            ),
        },
        { key: 'contato', header: 'Contato', render: (f) => (f.contato || f.telefone ? <span>{[f.contato, f.telefone].filter(Boolean).join(' · ')}</span> : VAZIO) },
        { key: 'prazoEntrega', header: 'Prazo', render: (f) => f.prazoEntrega || VAZIO },
        { key: 'categoria', header: 'Categoria de conta', render: (f) => f.categoria || VAZIO },
        { key: 'ativo', header: 'Situação', render: (f) => ATIVO(f.ativo) },
    ];
}

export function colunasDePrestadores(): DataTableColumn<PrestadorDeServico>[] {
    return [
        identidade<PrestadorDeServico>('Prestador'),
        { key: 'tipoServico', header: 'Tipo de serviço', sortable: true, render: (p) => (p.tipoServico ? <Badge tone="neutral">{p.tipoServico}</Badge> : VAZIO) },
        { key: 'email', header: 'E-mail', render: (p) => p.email || VAZIO },
        { key: 'categoria', header: 'Categoria de conta', render: (p) => p.categoria || VAZIO },
        { key: 'ativo', header: 'Situação', render: (p) => ATIVO(p.ativo) },
    ];
}
